import ReactGA from 'react-ga4';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ampli } from '../../../../../ampli';
import { Button } from '../../../../../components/common/buttons/Button';
import { Tooltip } from '../../../../../components/common/tooltip/Tooltip';
import { CallGuideModal } from '../../../../../components/custom/modal/popup/CallGuideModal';
import { flarelaneEventKeys } from '../../../../../constants/flarelaneEvent/flarelaneEvent.contant';
import { pageInfo } from '../../../../../constants/pageInfo/pageInfo.constant';
import { usePostUserSellTractorInquiries } from '../../../../../hooks/api/inquiry/mutations/usePostUserSellTractorInquiries';
import { usePopup } from '../../../../../hooks/popup/usePopup';
import { useVirtualNumberInquiry } from '../../../../../hooks/product/useVirtualNumberInquiry';
import { useCheckUserLoggedIn } from '../../../../../hooks/user/useCheckUserLoggedIn';
import { useGetUserProfile } from '../../../../../hooks/user/useGetUserProfile';
import { IOpenMarketProductDetail } from '../../../../../interfaces/OpenMarketProduct.interfaces';
import { getOpenMarketProductTitle } from '../../../../../utils/getter/getOpenMarketProductTitle.util';
import { postMessageToRN } from '../../../../../utils/RNCommunicator/postMessageToRN.util';

interface DirectDealRequestConsultButtonProps {
  product: IOpenMarketProductDetail;
  onPriceSuggestClick?: () => void;
  handleBumpButtonClick: (product: IOpenMarketProductDetail) => void;
}

export const DirectDealRequestConsultButton = ({
  product,
  onPriceSuggestClick,
  handleBumpButtonClick,
}: DirectDealRequestConsultButtonProps) => {
  const Popup = usePopup();
  const navigate = useNavigate();
  const location = useLocation();
  const { isUserLoggedIn } = useCheckUserLoggedIn();
  const { userProfile } = useGetUserProfile();

  const { mutate: postUserSellTractorInquiries } = usePostUserSellTractorInquiries();

  const {
    callWithVirtualNumber,
    isLoading: isVirtualNumberLoading,
    shouldShowModal,
    virtualNumber,
    closeModal,
  } = useVirtualNumberInquiry({
    productId: product?.id || '',
    productCode: product?.code || '',
    sellerPhoneNumber: product?.user?.phoneNumber || '',
  });

  // 비로그인이거나 판매자 정보가 없으면 둘 다 undefined라 같다고 판단되므로, 내 id가 있을 때만 비교한다.
  const isMyProduct = !!userProfile?.id && userProfile.id === product?.user?.id;

  const handlePriceSuggestButtonClick = () => {
    onPriceSuggestClick?.();
  };

  const handleRequestConsultButtonClick = () => {
    if (!product) {
      return;
    }

    ampli.마켓오픈마켓매물전화상담버튼클릭({
      '게시글 제목':
        getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      '매물 코드': product?.code,
    });

    postUserSellTractorInquiries({ reqBody: { userSellTractorId: product.id } });

    ampli.pvmProductInquiry({
      title: getOpenMarketProductTitle({ brandName: product.machine?.brand?.name, machine: product.machine }) ?? '',
      type: 'MemberToMember',
      page_type: 'odp',
      ps: product.machine.engineOutputInPs,
      manufacturedYear: product.manufacturedYear,
      operationTime: product.operationTime,
    });
    ReactGA.event('pvm_product_inquiry', {
      category: 'pvm',
      action: 'pvm_product_inquiry',
      value: 'MemberToMember',
    });

    if (window.ReactNativeWebView) {
      postMessageToRN({
        messageKey: 'flarelane_trackEvent',
        messageParam: flarelaneEventKeys.pvm_inquiry_user_sell_product,
        messageData: {
          productId: product.id,
          brandName: product.machine.brand?.name,
          machineModel: product.machine?.model,
        },
      });
    }

    // 안심번호로 전화 걸기
    callWithVirtualNumber();
  };

  if (!product) {
    return null;
  }

  return (
    <ButtonContainer>
      {!!product.priceNegotiationYn && !isMyProduct && (
        <ButtonWrapper>
          <ButtonWrapper>
            <Button
              borderType={'square'}
              color={'filledPrimaryLight'}
              size={'lg'}
              onClick={handlePriceSuggestButtonClick}
              icon={{
                fileName: 'Envelope',
                size: 'Large',
                fill: '#196EFF',
                position: 'left',
              }}
              text={{
                content: '문자하기',
                size: 'lg',
              }}
            />
          </ButtonWrapper>
        </ButtonWrapper>
      )}
      {isMyProduct && (
        <ButtonWrapper>
          <Tooltip
            type={'default'}
            size={'large'}
            customColor={{ background: '#505363', text: '#FFFFFF' }}
            text={'상단에 재등록 🔥'}
            style={{ left: 'initial' }}
          >
            <ButtonWrapper onClick={() => handleBumpButtonClick(product)}>
              <Button
                borderType={'square'}
                color={'filledGreyLight'}
                size={'lg'}
                text={{
                  content: '끌어올리기',
                  size: 'lg',
                }}
                onClick={() => handleBumpButtonClick(product)}
              />
            </ButtonWrapper>
          </Tooltip>
        </ButtonWrapper>
      )}
      {isMyProduct && (
        <ButtonWrapper>
          <Button
            borderType={'square'}
            color={'filledPrimaryStrong'}
            size={'lg'}
            onClick={() => navigate(pageInfo.myInfoSellTractorListPage.path)}
            text={{
              content: '매물 수정하기',
              size: 'lg',
            }}
          />
        </ButtonWrapper>
      )}
      {!isMyProduct && (
        <ButtonWrapper>
          <Tooltip
            type={'default'}
            size={'large'}
            customColor={{ background: '#505363', text: '#FFFFFF' }}
            text={'🤳🏻 전화로 문의 해보세요'}
            style={{ left: 'initial' }}
          >
            <ButtonWrapper>
              <Button
                borderType={'square'}
                color={'filledPrimaryStrong'}
                size={'lg'}
                onClick={handleRequestConsultButtonClick}
                isDisabled={isVirtualNumberLoading}
                icon={{
                  fileName: 'Phone',
                  size: 'Large',
                  fill: '#fff',
                  position: 'left',
                }}
                text={{
                  content: isVirtualNumberLoading ? '연결 중...' : '전화하기',
                  size: 'lg',
                }}
              />
            </ButtonWrapper>
          </Tooltip>
        </ButtonWrapper>
      )}
      <CallGuideModal isShow={shouldShowModal} virtualNumber={virtualNumber} onClose={closeModal} />
    </ButtonContainer>
  );
};

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 0;
`;
